import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtMoney, fmtDate } from "@/lib/caesar";
import { PageHeader, SectionLabel, Loading, EmptyState, Pill } from "@/components/common";
import { ListCard, MutedEmpty } from "@/components/widgets";
import { Brain, Sparkles, Send, RefreshCw, DollarSign, CheckSquare, FolderKanban, Users, Heart, AlertOctagon, Plus, Bell } from "lucide-react";
import { toast } from "sonner";

const TYPE_META = {
  money: { icon: DollarSign, color: "#10B981" },
  task: { icon: CheckSquare, color: "#F59E0B" },
  lead: { icon: Users, color: "#D4AF37" },
  project: { icon: FolderKanban, color: "#3B82F6" },
  relationship: { icon: Heart, color: "#E11D48" },
  decision: { icon: AlertOctagon, color: "#E11D48" },
};

function BriefBlock({ text }) {
  const HEADERS = ["FACTS", "AI ANALYSIS", "AI RECOMMENDATION"];
  const HEAD_COLOR = { FACTS: "#A3A3A3", "AI ANALYSIS": "#D4AF37", "AI RECOMMENDATION": "#10B981" };
  if (text.trim() === "INSUFFICIENT INFORMATION") {
    return <p className="text-samber font-num text-sm" data-testid="brief-insufficient">INSUFFICIENT INFORMATION — add operational data to generate a grounded brief.</p>;
  }
  const lines = text.split("\n");
  return (
    <div className="space-y-1 text-sm leading-relaxed" data-testid="brief-body">
      {lines.map((ln, i) => {
        const h = HEADERS.find((x) => ln.trim().toUpperCase() === x);
        if (h) return <div key={i} className="label-caps mt-4 first:mt-0" style={{ color: HEAD_COLOR[h] }}>{h}</div>;
        return <p key={i} className="whitespace-pre-wrap text-[#e8e8e8]">{ln}</p>;
      })}
    </div>
  );
}

export default function ChiefOfStaff() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [brief, setBrief] = useState("");
  const [briefAt, setBriefAt] = useState("");
  const [briefLoading, setBriefLoading] = useState(false);
  const [chat, setChat] = useState([]);
  const [q, setQ] = useState("");
  const [asking, setAsking] = useState(false);
  const [owners, setOwners] = useState([]);
  const [ownerSel, setOwnerSel] = useState({});
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    const [ov, us] = await Promise.all([api.get("/chief/overview"), api.get("/users").catch(() => ({ data: [] }))]);
    setData(ov.data);
    setOwners((us.data || []).map((u) => u.name).filter(Boolean));
  }, []);
  useEffect(() => { load(); }, [load]);

  const addTask = async (a) => {
    if (busy) return;
    setBusy(a.id);
    try {
      await api.post("/tasks", { title: a.title, notes: a.detail, owner: ownerSel[a.id] || a.owner || owners[0] || "",
        priority_label: "high", deadline: a.deadline || "", status: "todo", source: "chief_brief" });
      toast.success("Task created");
    } catch { toast.error("Could not create task"); } finally { setBusy(""); }
  };

  const addAlert = async (a) => {
    if (busy) return;
    setBusy(a.id + "-alert");
    try {
      await api.post("/alerts", { problem: a.title, why_it_matters: a.detail,
        recommended_action: a.detail, owner: ownerSel[a.id] || a.owner || "", severity: "high" });
      toast.success("Red Alert created");
    } catch { toast.error("Could not create alert"); } finally { setBusy(""); }
  };

  const genBrief = async () => {
    setBriefLoading(true);
    try {
      const { data } = await api.post("/chief/brief", {});
      setBrief(data.brief);
      setBriefAt(data.generated_at);
    } catch {
      toast.error("Could not generate brief");
    } finally {
      setBriefLoading(false);
    }
  };

  const ask = async () => {
    const query = q.trim();
    if (!query || asking) return;
    setQ("");
    setChat((c) => [...c, { role: "user", text: query }]);
    setAsking(true);
    try {
      const { data } = await api.post("/ask", { question: query });
      setChat((c) => [...c, { role: "szr", text: data.answer }]);
    } catch {
      setChat((c) => [...c, { role: "szr", text: "SZR could not process that. Try again." }]);
    } finally {
      setAsking(false);
    }
  };

  if (!data) return <Loading label="Briefing the Chief of Staff…" />;

  return (
    <div className="space-y-8" data-testid="chief-page">
      <PageHeader
        testId="chief-header"
        title="SZR AI Chief of Staff"
        subtitle="Grounded in your live SZR data — no fabrication."
        action={
          <button data-testid="chief-refresh" onClick={load} className="inline-flex items-center gap-2 text-sm text-[#A3A3A3] hover:text-gold transition-colors">
            <RefreshCw size={15} /> Refresh
          </button>
        }
      />

      {/* Top 5 */}
      <div>
        <SectionLabel>Top 5 Priorities Right Now</SectionLabel>
        {data.top5.length === 0 ? (
          <EmptyState testId="top5-empty" title="No priorities detected" hint="Add money, tasks, leads or projects and SZR will surface what matters." />
        ) : (
          <div className="space-y-2" data-testid="top5-list">
            {data.top5.map((c, i) => {
              const meta = TYPE_META[c.type] || TYPE_META.decision;
              const Icon = meta.icon;
              return (
                <button
                  key={c.id}
                  data-testid={`top5-item-${i}`}
                  onClick={() => navigate(c.link)}
                  className="w-full text-left flex items-center gap-4 bg-surface border border-edge rounded-xl px-4 py-3.5 hover:border-gold/40 hover:bg-surface2 transition-colors"
                >
                  <div className="font-display text-2xl font-extrabold text-gold w-7 shrink-0">{i + 1}</div>
                  <div className="w-9 h-9 rounded-lg grid place-items-center shrink-0" style={{ background: `${meta.color}1f`, border: `1px solid ${meta.color}44` }}>
                    <Icon size={17} style={{ color: meta.color }} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold truncate">{c.title}</p>
                    <p className="text-xs text-[#A3A3A3] truncate">{c.detail}</p>
                  </div>
                  <div className="text-right shrink-0">
                    {c.value > 0 && <div className="font-num text-sm text-sgreen">{fmtMoney(c.value)}</div>}
                    {c.deadline && <div className="font-num text-[0.65rem] text-[#777]">{fmtDate(c.deadline)}</div>}
                    {c.owner && <Pill color={meta.color} className="mt-0.5">{c.owner}</Pill>}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Daily Command Brief */}
      <div className="bg-surface border border-gold/30 rounded-xl p-5 sm:p-6" data-testid="brief-card">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Sparkles size={18} className="text-gold" />
            <span className="font-display text-xl font-bold">Daily Command Brief</span>
          </div>
          <button
            data-testid="generate-brief-btn"
            onClick={genBrief}
            disabled={briefLoading}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-gold text-ink font-bold text-sm hover:bg-[#e6c14e] transition-colors disabled:opacity-60"
          >
            {briefLoading ? <><span className="w-3 h-3 rounded-full bg-ink/70 animate-pulse" /> Analyzing…</> : <><Brain size={16} /> Generate Brief</>}
          </button>
        </div>
        {brief ? (
          <div className="mt-4 bg-ink border border-edge rounded-lg p-4">
            <BriefBlock text={brief} />
            {briefAt && <p className="text-[0.65rem] text-[#5a5a5a] font-num mt-3">Generated {new Date(briefAt).toLocaleString()}</p>}
          </div>
        ) : (
          <p className="text-sm text-[#A3A3A3] mt-3">Generate an AI briefing summarizing your money, priorities, decisions, and next moves — grounded strictly in SZR data.</p>
        )}
      </div>

      {/* Recommended Actions — one tap to execute */}
      {data.actions && data.actions.length > 0 && (
        <div>
          <SectionLabel>Recommended Actions — one tap to execute</SectionLabel>
          <div className="space-y-2" data-testid="recommended-actions">
            {data.actions.map((a) => (
              <div key={a.id} data-testid={`action-${a.id}`} className="bg-surface border border-edge rounded-xl px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold truncate">{a.title}</p>
                  <p className="text-xs text-[#A3A3A3] truncate">{a.detail}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <select
                    data-testid={`action-owner-${a.id}`}
                    value={ownerSel[a.id] || a.owner || owners[0] || ""}
                    onChange={(e) => setOwnerSel((s) => ({ ...s, [a.id]: e.target.value }))}
                    className="h-9 rounded-md bg-ink border border-edge px-2 text-xs text-[#FAFAFA]"
                  >
                    {owners.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                  <button data-testid={`action-task-${a.id}`} onClick={() => addTask(a)} disabled={busy === a.id}
                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-gold text-ink text-xs font-bold hover:bg-[#e6c14e] transition-colors disabled:opacity-60">
                    <Plus size={13} /> Task
                  </button>
                  <button data-testid={`action-alert-${a.id}`} onClick={() => addAlert(a)} disabled={busy === a.id + "-alert"}
                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-sred/50 text-sred text-xs font-bold hover:bg-sred/10 transition-colors disabled:opacity-60">
                    <Bell size={13} /> Alert
                  </button>
                </div>
              </div>
            ))}
          </div>
          {data.actions.length < 3 && (
            <p className="text-xs text-[#5a5a5a] mt-2">Add more money, tasks, leads or projects and SZR will surface more actions here.</p>
          )}
        </div>
      )}

      {/* Decisions / Waiting / Follow-ups */}
      <div className="grid md:grid-cols-3 gap-4">
        <ListCard title="Decisions Needed" count={data.decisions_needed.length} testId="decisions-needed">
          {data.decisions_needed.length === 0 ? <MutedEmpty>No open decisions.</MutedEmpty> :
            data.decisions_needed.map((d) => (
              <div key={d.id} className="px-3 py-2.5 hover:bg-surface2 rounded-lg">
                <p className="text-sm">{d.title}</p>
                <span className="label-caps text-[0.55rem] text-samber">{d.detail}</span>
              </div>
            ))}
        </ListCard>

        <ListCard title="Waiting On" count={data.waiting_on.length} testId="waiting-on">
          {data.waiting_on.length === 0 ? <MutedEmpty>Not waiting on anyone.</MutedEmpty> :
            data.waiting_on.map((w) => (
              <div key={w.id} className="px-3 py-2.5 hover:bg-surface2 rounded-lg">
                <p className="text-sm">{w.title}</p>
                <span className="label-caps text-[0.55rem]">{w.owner || "—"}</span>
              </div>
            ))}
        </ListCard>

        <ListCard title="Follow-Ups Due" count={data.follow_ups.length} testId="follow-ups">
          {data.follow_ups.length === 0 ? <MutedEmpty>No follow-ups due.</MutedEmpty> :
            data.follow_ups.map((f) => (
              <div key={f.id} className="px-3 py-2.5 hover:bg-surface2 rounded-lg cursor-pointer" onClick={() => navigate("/people")}>
                <p className="text-sm">{f.name}</p>
                <span className="label-caps text-[0.55rem]">{f.detail}</span>
              </div>
            ))}
        </ListCard>
      </div>

      {/* Ask SZR chat */}
      <div>
        <SectionLabel>Ask SZR</SectionLabel>
        <div className="bg-surface border border-edge rounded-xl p-4" data-testid="ask-chat">
          {chat.length === 0 && (
            <p className="text-sm text-[#A3A3A3] px-1 pb-3">Ask anything about your operation — money owed, hot leads, stalled projects, who to call.</p>
          )}
          <div className="space-y-3 max-h-[46vh] overflow-y-auto">
            {chat.map((m, i) => (
              <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                <div
                  data-testid={`chat-${m.role}-${i}`}
                  className={m.role === "user"
                    ? "bg-gold text-ink rounded-lg rounded-br-sm px-3.5 py-2 text-sm max-w-[85%]"
                    : "bg-ink border border-edge rounded-lg rounded-bl-sm px-3.5 py-2 text-sm max-w-[90%] whitespace-pre-wrap leading-relaxed"}
                >
                  {m.text}
                </div>
              </div>
            ))}
            {asking && <div className="flex items-center gap-2 text-[#A3A3A3] text-sm px-1"><span className="w-2.5 h-2.5 rounded-full bg-gold animate-pulse" /> SZR is analyzing…</div>}
          </div>
          <div className="flex items-center gap-2 mt-4 bg-ink border border-edge rounded-lg px-3">
            <input
              data-testid="chat-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && ask()}
              placeholder="Ask about your operation…"
              className="flex-1 bg-transparent h-12 outline-none text-[#FAFAFA] placeholder:text-[#5a5a5a]"
            />
            <button data-testid="chat-send" onClick={ask} disabled={asking} className="text-gold disabled:opacity-50"><Send size={18} /></button>
          </div>
        </div>
      </div>
    </div>
  );
}
